import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useChowaTheme } from './chowaThemeContext';
import classNames from 'classnames';

const ChowaSidebar = ({ sidebarConfig = { title: 'Dashboard', menuItems: [] } }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { getCurrentTheme, currentThemeName, setSeriesColor, selectedSeriesColor, isDark, tmsConnectionState } = useChowaTheme();
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [selectedModel, setSelectedModel] = useState(null);

  const bgColors = {
    blue: 'bg-blue-600',
    orange: 'bg-orange-600', 
    purple: 'bg-purple-600',
    gray: 'bg-gray-800'
  };

  const toggleExpand = (path) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(path)) {
        newSet.delete(path);
      } else {
        newSet.add(path);
      }
      return newSet;
    });
  };

  const handleItemClick = (item) => {
    const hasSubItems = item.subItems && item.subItems.length > 0;
    
    if (hasSubItems) {
      toggleExpand(item.path);
    } else if (item.external) {
      // 外部リンクの場合は新しいタブで開く
      window.open(item.path, '_blank');
    } else {
      // 通常のメニュー項目の場合は通常の遷移
      navigate(item.path);
    }
  };

  const handleSubItemClick = (parentItem, subItem) => {
    const hasSubItems = subItem.subItems && subItem.subItems.length > 0;
    
    if (hasSubItems) {
      // サブアイテムにさらに子がある場合は展開/折りたたみ
      toggleExpand(`${parentItem.path}/${subItem.path}`);
      
      // シリーズ識別子があれば設定
      if (subItem.series) {
        setSeriesColor(subItem.series);
      }
    } else {
      // TMS間の切り替えかチェック
      if (parentItem.path === 'tms' && tmsConnectionState.isConnected) {
        // 現在のTMSと異なるTMSに切り替えようとしているかチェック
        const targetModelId = subItem.path;
        if (tmsConnectionState.currentModelId !== targetModelId) {
          // 接続中の場合は警告メッセージを表示
          alert(
            `${tmsConnectionState.currentModelId?.toUpperCase()}に接続中です。\n\n先に切断ボタンを押してから移動してください。`
          );
          return; // 移動を中止
        }
      }
      
      // シリーズ識別子があれば設定
      if (subItem.series) {
        setSeriesColor(subItem.series);
      }
      // ルートに移動
      navigate(`${parentItem.path}/${subItem.path}`);
    }
  };

  const handleModelClick = (parentItem, seriesItem, modelItem) => {
    // モデル選択時の処理
    setSelectedModel(modelItem.model);
    
    // シリーズ色を設定
    if (modelItem.series) {
      setSeriesColor(modelItem.series);
    }
    
    // モデル選択のイベントを発火（ダッシュボードに通知）
    window.dispatchEvent(new CustomEvent('chowaModelSelect', {
      detail: { model: modelItem.model, series: modelItem.series }
    }));
    
    // ルートに移動
    navigate(`${parentItem.path}/${seriesItem.path}/${modelItem.path}`);
  };

  const renderSubItems = (parentItem, subItems, depth = 1) => {
    return subItems.map((subItem) => {
      const SubIcon = subItem.icon;
      const fullPath = `${parentItem.path}/${subItem.path}`;
      const isExpanded = expandedItems.has(fullPath);
      const hasSubItems = subItem.subItems && subItem.subItems.length > 0;
      const isSubItemActive = location.pathname.includes(fullPath);
      const isSeriesSelected = subItem.series && selectedSeriesColor === subItem.series;
      
      return (
        <div key={subItem.path}>
          <div
            className={classNames(
              'flex items-center px-4 py-2 rounded-lg transition-colors cursor-pointer',
              (isSubItemActive || isSeriesSelected) && (selectedSeriesColor == null && !isDark)
                ? ''
                : [
                    'hover:bg-white hover:bg-opacity-10',
                    (isSubItemActive || isSeriesSelected) ? 'bg-white bg-opacity-10' : ''
                  ]
            )}
            style={
              (isSubItemActive || isSeriesSelected) && (selectedSeriesColor == null && !isDark)
                ? { backgroundColor: 'rgba(34,34,34,0.12)' }
                : undefined
            }
            onClick={() => handleSubItemClick(parentItem, subItem)}
          >
            <SubIcon className="w-5 h-5 mr-3" />
            <span>{subItem.name}</span>
            {hasSubItems && (
              <svg
                className={`w-4 h-4 ml-auto transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            )}
          </div>

          {/* さらに深い階層のサブメニュー（モデル） */}
          {hasSubItems && isExpanded && (
            <div className="mt-1">
              {subItem.subItems.map((modelItem) => {
                const ModelIcon = modelItem.icon;
                const modelPath = `${fullPath}/${modelItem.path}`;
                const isModelActive = location.pathname.includes(modelPath);
                const isModelSelected = selectedModel === modelItem.model;
                
                return (
                  <div
                    key={modelItem.path}
                    className={classNames(
                      'flex items-center px-4 py-2 rounded-lg transition-colors cursor-pointer',
                      'hover:bg-white hover:bg-opacity-10',
                      (isModelActive || isModelSelected) ? 'bg-white bg-opacity-10' : ''
                    )}
                    style={{ marginLeft: `${(depth + 1) * 12}px` }}
                    onClick={() => handleModelClick(parentItem, subItem, modelItem)}
                  >
                    <ModelIcon className="w-4 h-4 mr-3" />
                    <span>{modelItem.name}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    });
  };

  const renderMenuItem = (item) => {
    const Icon = item.icon;
    const isExpanded = expandedItems.has(item.path);
    const hasSubItems = item.subItems && item.subItems.length > 0;

    return (
      <div key={item.path}>
        <div
          className={classNames(
            'flex items-center px-4 py-3 rounded-lg transition-colors cursor-pointer',
            (location.pathname.includes(item.path) && selectedSeriesColor == null && !isDark)
              ? ''
              : [
                  'hover:bg-white hover:bg-opacity-10',
                  location.pathname.includes(item.path) ? 'bg-white bg-opacity-10' : ''
                ]
          )}
          style={
            (location.pathname.includes(item.path) && selectedSeriesColor == null && !isDark)
              ? { backgroundColor: 'rgba(34,34,34,0.12)' }
              : undefined
          }
          onClick={() => handleItemClick(item)}
        >
          <Icon className="w-6 h-6 mr-3" />
          <span>{item.name}</span>
          {hasSubItems && (
            <svg
              className={`w-4 h-4 ml-auto transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          )}
        </div>

        {/* サブメニュー */}
        {hasSubItems && isExpanded && (
          <div className="ml-6 mt-1">
            {renderSubItems(item, item.subItems)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className={
        "h-full " +
        (selectedSeriesColor == null && !isDark
          ? "bg-white text-black border-r border-r-black"
          : bgColors[currentThemeName] + " text-white")
      }
    >
      {/* ロゴ */}
      <div className="px-6 py-4 border-b border-opacity-20 border-white">
        <h1 className="text-xl font-bold">{sidebarConfig.title}</h1>
      </div>

      {/* ナビゲーション */}
      <nav className="mt-6 px-4">
        {sidebarConfig.menuItems.map(renderMenuItem)}
      </nav>
    </div>
  );
};

export default ChowaSidebar; 